/**
 * FormAForm - TASK-376
 * 4-part form following CDB 20 structure:
 * Part A (Account Holder ID), Part B (Beneficial Owner declaration),
 * Part C (CDB 20 Declarations), Part D (Signature).
 * Auto-populated from approved KYC data.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  User,
  UserCheck,
  ShieldCheck,
  PenTool,
  Save,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CollapsibleSectionHeader } from './CollapsibleSectionHeader';
import { PartAAccountHolder } from './PartAAccountHolder';
import { PartBBeneficialOwner } from './PartBBeneficialOwner';
import { PartDSignature } from './PartDSignature';
import type { BeneficialOwnerEntry } from './BeneficialOwnerSection';
import { CDB20DeclarationChecklist } from './CDB20DeclarationChecklist';
import type { CDB20Declarations } from './CDB20DeclarationChecklist';
import kycService from '../../../services/kycService';

const formACompletionSchema = z.object({
  account_holder: z.object({
    name: z.string().min(1, 'Account holder name is required'),
  }),
  beneficial_owner: z.object({
    name: z.string().min(1, 'Beneficial owner name is required'),
  }),
  signature: z.object({
    signatory_name: z.string().min(1, 'Signatory name is required'),
    date: z.string().min(1, 'Signature date is required'),
    signature_data: z.string().min(1, 'Signature is required'),
  }),
});

type ValidationErrors = Record<string, string>;

interface FormAFormProps {
  clientId: string;
  clientName: string;
  initialData?: FormAFormData | null;
  onSaved?: () => void;
}

export interface FormAFormData {
  account_holder: {
    name?: string | null;
    address?: Record<string, any> | null;
    source?: string;
  };
  beneficial_owner: {
    owner_type: 'self' | 'third_party';
    name?: string | null;
    date_of_birth?: string | null;
    nationality?: string | null;
    domicile?: string | null;
    percentage: number;
    is_pep: boolean;
    pep_position?: string | null;
    pep_country?: string | null;
    source?: string;
  };
  additional_owners?: BeneficialOwnerEntry[];
  declarations: CDB20Declarations;
  signature: {
    date?: string | null;
    signature_data?: string | null;
    signatory_name?: string | null;
  };
  status: 'draft' | 'completed';
  generated_at?: string;
  generated_from?: string;
}

const PartHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  partLabel: string;
  isOpen: boolean;
  onToggle: () => void;
  isInherited?: boolean;
}> = (props) => (
  <CollapsibleSectionHeader
    icon={props.icon}
    title={props.title}
    label={props.partLabel}
    isOpen={props.isOpen}
    onToggle={props.onToggle}
    isInherited={props.isInherited}
    badgeClassName="bg-indigo-100 text-indigo-700"
  />
);

const DEFAULT_DECLARATIONS: CDB20Declarations = {
  identity_confirmed: false,
  beneficial_owner_confirmed: false,
  source_of_funds_confirmed: false,
  tax_compliance_confirmed: false,
  pep_declaration_confirmed: false,
  information_accuracy_confirmed: false,
  reporting_obligation_confirmed: false,
};

const DEFAULT_DATA: FormAFormData = {
  account_holder: { name: null, address: null, source: 'manual' },
  beneficial_owner: {
    owner_type: 'self',
    name: null,
    date_of_birth: null,
    nationality: null,
    domicile: null,
    percentage: 100,
    is_pep: false,
    pep_position: null,
    pep_country: null,
    source: 'manual',
  },
  additional_owners: [],
  declarations: { ...DEFAULT_DECLARATIONS },
  signature: { date: null, signature_data: null, signatory_name: null },
  status: 'draft',
};

export const FormAForm: React.FC<FormAFormProps> = ({
  clientId,
  clientName,
  initialData,
  onSaved,
}) => {
  const [formData, setFormData] = useState<FormAFormData>(initialData || DEFAULT_DATA);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [openParts, setOpenParts] = useState<Record<string, boolean>>({
    A: true,
    B: false,
    C: false,
    D: false,
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const togglePart = useCallback((part: string) => {
    setOpenParts((prev) => ({ ...prev, [part]: !prev[part] }));
  }, []);

  const updateAccountHolder = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        account_holder: { ...prev.account_holder, [field]: value },
      }));
    },
    []
  );

  const updateBeneficialOwner = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        beneficial_owner: { ...prev.beneficial_owner, [field]: value },
      }));
    },
    []
  );

  const handleDeclarationChange = useCallback(
    (key: keyof CDB20Declarations, value: boolean) => {
      setFormData((prev) => ({
        ...prev,
        declarations: { ...prev.declarations, [key]: value },
      }));
    },
    []
  );

  const updateSignature = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => ({
        ...prev,
        signature: { ...prev.signature, [field]: value },
      }));
    },
    []
  );

  const handleAdditionalOwnerChange = useCallback(
    (index: number, field: keyof BeneficialOwnerEntry, value: string | number) => {
      setFormData((prev) => {
        const owners = [...(prev.additional_owners || [])];
        owners[index] = { ...owners[index], [field]: value };
        return { ...prev, additional_owners: owners };
      });
    },
    []
  );

  const handleAddOwner = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      beneficial_owner: { ...prev.beneficial_owner, owner_type: 'third_party' },
      additional_owners: [
        ...(prev.additional_owners || []),
        {
          name: '',
          date_of_birth: '',
          nationality: '',
          address: '',
          ownership_percentage: 0,
          source: 'manual',
        },
      ],
    }));
  }, []);

  const handleRemoveOwner = useCallback((index: number) => {
    setFormData((prev) => {
      const owners = (prev.additional_owners || []).filter((_, i) => i !== index);
      return {
        ...prev,
        additional_owners: owners,
        beneficial_owner: {
          ...prev.beneficial_owner,
          owner_type: owners.length === 0 ? 'self' : 'third_party',
        },
      };
    });
  }, []);

  const allDeclarationsChecked = Object.values(formData.declarations).every(Boolean);

  const validateForm = useCallback((): boolean => {
    const result = formACompletionSchema.safeParse(formData);
    if (result.success) {
      setErrors({});
      return true;
    }
    const fieldErrors: ValidationErrors = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      fieldErrors[path] = issue.message;
    }
    setErrors(fieldErrors);
    return false;
  }, [formData]);

  const handleSave = async (status: 'draft' | 'completed') => {
    if (status === 'completed') {
      if (!allDeclarationsChecked) {
        toast.error('All CDB 20 declarations must be confirmed before completion');
        setOpenParts((prev) => ({ ...prev, C: true }));
        return;
      }
      if (!validateForm()) {
        toast.error('Please fill in all required fields before completing');
        setOpenParts((prev) => ({ ...prev, A: true, B: true, D: true }));
        return;
      }
    }

    setSaving(true);
    try {
      const dataToSave = { ...formData, status };
      await kycService.saveFormA(clientId, dataToSave);
      toast.success(
        status === 'completed'
          ? 'Form A completed and saved'
          : 'Form A draft saved'
      );
      setErrors({});
      setFormData(dataToSave);
      onSaved?.();
    } catch (error: unknown) {
      const message =
        (error as Record<string, any>)?.response?.data?.detail || 'Failed to save Form A';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const isInherited = (source?: string) => source === 'inherited';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Form A — Beneficial Owner Declaration
          </h2>
          <p className="text-sm text-gray-500">
            {clientName} — CDB 20 Compliance —{' '}
            {formData.status === 'completed' ? 'Completed' : 'Draft'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave('completed')}
            disabled={saving || !allDeclarationsChecked}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!allDeclarationsChecked ? 'All CDB 20 declarations must be confirmed' : ''}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Complete
          </button>
        </div>
      </div>

      {/* Part A: Account Holder */}
      <div className="border border-gray-200 rounded-lg">
        <PartHeader
          icon={User}
          title="Account Holder Identification"
          partLabel="A"
          isOpen={!!openParts.A}
          onToggle={() => togglePart('A')}
          isInherited={isInherited(formData.account_holder.source)}
        />
        {openParts.A && (
          <PartAAccountHolder
            data={formData.account_holder}
            onChange={updateAccountHolder}
            errors={errors}
            isInherited={isInherited(formData.account_holder.source)}
          />
        )}
      </div>

      {/* Part B: Beneficial Owner */}
      <div className="border border-gray-200 rounded-lg">
        <PartHeader
          icon={UserCheck}
          title="Beneficial Owner Declaration"
          partLabel="B"
          isOpen={!!openParts.B}
          onToggle={() => togglePart('B')}
          isInherited={isInherited(formData.beneficial_owner.source)}
        />
        {openParts.B && (
          <PartBBeneficialOwner
            data={formData.beneficial_owner}
            additionalOwners={formData.additional_owners || []}
            onChange={updateBeneficialOwner}
            onAddOwner={handleAddOwner}
            onRemoveOwner={handleRemoveOwner}
            onAdditionalOwnerChange={handleAdditionalOwnerChange}
            errors={errors}
            isInherited={isInherited(formData.beneficial_owner.source)}
          />
        )}
      </div>

      {/* Part C: CDB 20 Declarations */}
      <div className="border border-gray-200 rounded-lg">
        <PartHeader
          icon={ShieldCheck}
          title="CDB 20 Declarations"
          partLabel="C"
          isOpen={!!openParts.C}
          onToggle={() => togglePart('C')}
        />
        {openParts.C && (
          <div className="px-4 pb-4">
            <CDB20DeclarationChecklist
              declarations={formData.declarations}
              onChange={handleDeclarationChange}
              showValidation={!allDeclarationsChecked}
            />
          </div>
        )}
      </div>

      {/* Part D: Signature */}
      <div className="border border-gray-200 rounded-lg">
        <PartHeader
          icon={PenTool}
          title="Signature"
          partLabel="D"
          isOpen={!!openParts.D}
          onToggle={() => togglePart('D')}
        />
        {openParts.D && (
          <PartDSignature
            data={formData.signature}
            onChange={updateSignature}
            errors={errors}
            allDeclarationsChecked={allDeclarationsChecked}
          />
        )}
      </div>
    </div>
  );
};

export default FormAForm;
